import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Lock, Menu, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";

const links = [
    { to: "/shop", label: "Shop", testid: "nav-shop-link" },
    { to: "/#manifesto", label: "Manifesto", testid: "nav-manifesto-link" },
];

export const Header = () => {
    const { count, setOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();

    return (
        <motion.header
            data-testid="nav-header"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 left-0 right-0 z-50 border-b border-red-950/50 bg-black/80 backdrop-blur-xl"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12">
                <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center border border-red-900/70 bg-red-950/30 font-display text-sm font-bold text-red-500 transition-colors duration-300 group-hover:bg-red-900/40">
                        LS
                    </span>
                    <span className="flex flex-col leading-none">
                        <span className="font-display text-sm font-bold tracking-[0.3em] text-bone">
                            LOGAN'S
                        </span>
                        <span className="mt-1 font-mono2 text-[8px] uppercase tracking-[0.25em] text-red-500">
                            Reptiles &amp; Exotics
                        </span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-10 md:flex">
                    {links.map((l) => (
                        <Link
                            key={l.testid}
                            to={l.to}
                            data-testid={l.testid}
                            className={`font-mono2 text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:text-red-400 ${
                                location.pathname === l.to ? "text-red-500" : "text-ash"
                            }`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/admin"
                        data-testid="nav-admin-login-button"
                        className="hidden items-center gap-2 border border-red-950/60 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:border-red-800 hover:text-red-400 sm:flex"
                    >
                        <Lock className="h-3 w-3" /> Admin
                    </Link>
                    <button
                        data-testid="nav-cart-trigger"
                        onClick={() => setOpen(true)}
                        className="relative flex items-center gap-2 bg-crimson px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-red-700"
                    >
                        <ShoppingBag className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Enclosure</span>
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-bone font-mono2 text-[10px] font-bold text-black animate-pulse-glow"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="nav-mobile-menu"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        className="text-ash md:hidden"
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="border-t border-red-950/50 bg-black/95 px-6 py-6 md:hidden">
                    {links.map((l) => (
                        <Link
                            key={l.testid}
                            to={l.to}
                            data-testid={`${l.testid}-mobile`}
                            onClick={() => setMobileOpen(false)}
                            className="block py-3 font-mono2 text-xs uppercase tracking-[0.25em] text-ash hover:text-red-400"
                        >
                            {l.label}
                        </Link>
                    ))}
                    <Link
                        to="/admin"
                        data-testid="nav-admin-login-button-mobile"
                        onClick={() => setMobileOpen(false)}
                        className="block py-3 font-mono2 text-xs uppercase tracking-[0.25em] text-ash hover:text-red-400"
                    >
                        Admin
                    </Link>
                </div>
            )}
        </motion.header>
    );
};
